import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, KeyRound, ArrowRight, LogOut, ArrowLeft, Copy, Share2, Check, PartyPopper } from "lucide-react";

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteFromUrl = (searchParams.get("invite") || "").toUpperCase().trim();
  const [mode, setMode] = useState(inviteFromUrl ? "join" : null); // 'create' | 'join' | null
  const [name, setName] = useState("");
  const [code, setCode] = useState(inviteFromUrl);
  const [loading, setLoading] = useState(false);
  const hasGroup = !!(user?.active_group_id || user?.group_id);
  const [createdGroup, setCreatedGroup] = useState(null); // {id, name, invite_code} after creation

  useEffect(() => {
    // Auto-join via invite link
    if (inviteFromUrl && user) {
      setMode("join");
      setCode(inviteFromUrl);
    }
  }, [inviteFromUrl, user]);

  const onCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/groups", { name });
      await refresh();
      setCreatedGroup(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onJoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/groups/join", { invite_code: code.toUpperCase().trim() });
      await refresh();
      if (data.pending) {
        toast.info(data.message || "Richiesta inviata. Attendi l'approvazione.");
        nav("/home");
      } else {
        toast.success("Sei nel gruppo!");
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inviteUrl = createdGroup
    ? `${window.location.origin}/?invite=${encodeURIComponent(createdGroup.invite_code)}`
    : "";

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(createdGroup.invite_code);
      toast.success("Codice copiato!");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link copiato!");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const shareInvite = async () => {
    const text = `Unisciti al gruppo "${createdGroup.name}" su Hopzigo! Codice: ${createdGroup.invite_code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Hopzigo", text, url: inviteUrl });
      } catch (e) {
        // user cancelled - ignore
      }
    } else {
      await copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#8A91A8] mb-1">Ciao {user?.name?.split(" ")[0]}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              {hasGroup ? "Aggiungi un gruppo" : "Il tuo gruppo"}
            </h1>
          </div>
          {hasGroup ? (
            <Link to="/dashboard" className="w-10 h-10 grid place-items-center rounded-xl bg-white border border-[#ECEEF5] text-[#1A2A6C] hover:bg-[#F5F6FA] shadow-[0_2px_8px_rgba(20,32,80,0.04)]" data-testid="onboarding-back-to-dashboard" title="Torna alla dashboard">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          ) : (
            <button onClick={logout} className="w-10 h-10 grid place-items-center rounded-xl bg-white border border-[#ECEEF5] text-[#8A91A8] hover:bg-[#F5F6FA] shadow-[0_2px_8px_rgba(20,32,80,0.04)]" data-testid="onboarding-logout" title="Esci">
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>

        {createdGroup && (
          <div className="brut-card p-6 sm:p-8 bg-gradient-to-br from-[#1A2A6C] to-[#2D3F8F] text-white border-0 anim-pop-in" data-testid="onboarding-created-screen">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-12 grid place-items-center rounded-2xl bg-white/15 backdrop-blur border border-white/20">
                <PartyPopper className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Gruppo creato!</h2>
                <p className="text-white/85">{createdGroup.name}</p>
              </div>
            </div>

            <p className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-2 mt-4">Codice invito</p>
            <div className="font-heading font-bold text-4xl sm:text-5xl tracking-[0.3em] bg-white text-[#1A2A6C] rounded-2xl px-5 py-4 shadow-[0_8px_24px_rgba(0,0,0,0.18)] text-center" data-testid="onboarding-created-code">
              {createdGroup.invite_code}
            </div>

            <p className="text-sm text-white/80 mt-4 leading-snug">
              Condividi questo codice (o il link) con gli altri genitori per farli entrare nel gruppo.
            </p>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <button onClick={copyCode} className="px-3 py-3 rounded-2xl bg-white/15 backdrop-blur border border-white/20 text-white font-semibold hover:bg-white/25 inline-flex items-center justify-center gap-1.5 text-sm" data-testid="onboarding-created-copy-code">
                <Copy className="w-4 h-4" /> Codice
              </button>
              <button onClick={copyLink} className="px-3 py-3 rounded-2xl bg-white/15 backdrop-blur border border-white/20 text-white font-semibold hover:bg-white/25 inline-flex items-center justify-center gap-1.5 text-sm" data-testid="onboarding-created-copy-link">
                <Copy className="w-4 h-4" /> Link
              </button>
              <button onClick={shareInvite} className="px-3 py-3 rounded-2xl bg-white text-[#1A2A6C] font-semibold hover:bg-[#F5F6FA] inline-flex items-center justify-center gap-1.5 text-sm shadow-[0_4px_14px_rgba(0,0,0,0.12)]" data-testid="onboarding-created-share">
                <Share2 className="w-4 h-4" /> Condividi
              </button>
            </div>

            <p className="text-[11px] text-white/70 mt-3 break-all">{inviteUrl}</p>

            <button onClick={() => nav("/dashboard")} className="mt-6 w-full px-5 py-3.5 rounded-2xl bg-white text-[#1A2A6C] font-bold inline-flex items-center justify-center gap-2 shadow-[0_6px_18px_rgba(0,0,0,0.18)] hover:bg-[#F5F6FA]" data-testid="onboarding-created-continue">
              Vai alla dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {!createdGroup && !mode && (
          <div className="space-y-4 anim-slide-up">
            <button
              onClick={() => setMode("join")}
              className="w-full brut-card brut-card-hover text-left p-6 sm:p-8 group"
              data-testid="onboarding-join-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 grid place-items-center rounded-2xl bg-[#E5E9F5] text-[#1A2A6C] shrink-0">
                  <KeyRound className="w-6 h-6" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl font-bold tracking-tight">Ho un codice invito</h2>
                  <p className="text-[#8A91A8] mt-1">Entra in un gruppo già esistente.</p>
                </div>
                <ArrowRight className="w-6 h-6 mt-3 text-[#1A2A6C] group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
            <button
              onClick={() => setMode("create")}
              className="w-full brut-card brut-card-hover text-left p-6 sm:p-8 group"
              data-testid="onboarding-create-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 grid place-items-center rounded-2xl bg-[#1A2A6C] text-white shrink-0 shadow-[0_6px_18px_rgba(26,42,108,0.32)]">
                  <Plus className="w-6 h-6" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl font-bold tracking-tight">Crea un nuovo gruppo</h2>
                  <p className="text-[#8A91A8] mt-1">Ricevi un codice da condividere con gli altri genitori.</p>
                </div>
                <ArrowRight className="w-6 h-6 mt-3 text-[#1A2A6C] group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>
        )}

        {!createdGroup && mode === "create" && (
          <form onSubmit={onCreate} className="brut-card p-6 sm:p-8 anim-pop-in">
            <div className="flex items-center gap-2 mb-5">
              <Users className="w-5 h-5 text-[#1A2A6C]" />
              <h2 className="font-heading text-2xl font-bold">Crea il gruppo</h2>
            </div>
            <label className="brut-label">Nome del gruppo</label>
            <input
              className="brut-input mb-5"
              placeholder="Es. Scuola 3°B - Anno 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="onboarding-group-name-input"
            />
            <div className="flex gap-3">
              <button type="button" onClick={() => setMode(null)} className="brut-btn brut-btn-ghost" data-testid="onboarding-back-button">
                Indietro
              </button>
              <button type="submit" className="brut-btn flex-1" disabled={loading} data-testid="onboarding-create-submit">
                {loading ? "Creazione…" : "Crea gruppo"}
              </button>
            </div>
          </form>
        )}

        {!createdGroup && mode === "join" && (
          <form onSubmit={onJoin} className="brut-card p-6 sm:p-8 anim-pop-in">
            <div className="flex items-center gap-2 mb-5">
              <KeyRound className="w-5 h-5 text-[#1A2A6C]" />
              <h2 className="font-heading text-2xl font-bold">Inserisci il codice</h2>
            </div>
            <label className="brut-label">Codice invito (6 caratteri)</label>
            <input
              className="brut-input mb-5 font-mono text-2xl tracking-[0.4em] uppercase text-center"
              placeholder="ABC123"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={8}
              required
              data-testid="onboarding-invite-code-input"
            />
            <div className="flex gap-3">
              <button type="button" onClick={() => setMode(null)} className="brut-btn brut-btn-ghost" data-testid="onboarding-back-button">
                Indietro
              </button>
              <button type="submit" className="brut-btn flex-1" disabled={loading} data-testid="onboarding-join-submit">
                {loading ? "Verifico…" : "Entra nel gruppo"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
